import { supabase } from '@/integrations/supabase/client';
import { format, subDays, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { buildUtcMonthBuckets, assignToBucket, fetchAllProcurementsInRange } from '@/lib/procurement-trend-utils';

export interface SupplierUnderwritingData {
  entity_id: string;
  supplier_name: string;
  supplier_type: string;
  region: string | null;
  coordinator_name: string | null;
  total_orders: number;
  total_value_birr: number;
  fulfillment_rate: number | null;
  damage_rate: number | null;
  payment_performance: number | null;
  avg_payment_term_days: number | null;
  consistency_variability_score: number | null;
}

// Procurement Performance Report Data Types
export interface ProcurementStatusStats {
  status: string;
  count: number;
  percentage: number;
  total_value: number;
}

export interface ProcurementTrend {
  month: string;
  count: number;
  total_value: number;
  avg_processing_time: number;
}

export interface TopProduct {
  product_name: string;
  count: number;
  total_quantity: number;
  total_value: number;
}

export interface RejectionAnalysis {
  rejection_source: string;
  count: number;
  percentage: number;
  common_reasons: string[];
}

export interface ProcurementPerformanceData {
  statusStats: ProcurementStatusStats[];
  monthlyTrends: ProcurementTrend[];
  topProducts: TopProduct[];
  rejectionAnalysis: RejectionAnalysis[];
  averageProcessingTime: number;
  totalValue: number;
  totalCount: number;
  totalQuantity: number;
}

// Supplier Performance Report Data Types
export interface SupplierRanking {
  supplier_id: string;
  supplier_name: string;
  completion_rate: number;
  total_procurements: number;
  completed_procurements: number;
  total_value: number;
  avg_delivery_time: number;
  rejection_rate: number;
  quality_score: number;
}

export interface DeliveryPerformance {
  supplier_id: string;
  supplier_name: string;
  on_time_deliveries: number;
  late_deliveries: number;
  avg_delay_days: number;
  performance_trend: 'improving' | 'declining' | 'stable';
}

export interface SupplierPerformanceData {
  supplierRankings: SupplierRanking[];
  deliveryPerformance: DeliveryPerformance[];
  topPerformers: SupplierRanking[];
  underPerformers: SupplierRanking[];
}

// Procurement Performance Report Service
export const getProcurementPerformanceData = async (
  startDate?: Date,
  endDate?: Date
): Promise<ProcurementPerformanceData> => {
  const start = startDate || startOfYear(new Date());
  const end = endDate || new Date();

  console.log('Fetching procurement performance data from', start, 'to', end);

  // Paginated fetch to avoid 1000-row truncation
  const procurements = await fetchAllProcurementsInRange(
    supabase,
    start.toISOString(),
    end.toISOString(),
    'status, total_amount, total_quantity, created_at, delivery_date, product_name, rejection_source'
  );

  console.log(`Fetched ${procurements.length} procurements (paginated) for report`);

  // Calculate status statistics
  const statusGroups = procurements?.reduce((acc, p) => {
    const status = p.status || 'unknown';
    if (!acc[status]) {
      acc[status] = { count: 0, total_value: 0 };
    }
    acc[status].count += 1;
    acc[status].total_value += p.total_amount || 0;
    return acc;
  }, {} as Record<string, { count: number; total_value: number }>);

  const totalCount = procurements.length;
  const statusStats: ProcurementStatusStats[] = Object.entries(statusGroups || {}).map(([status, data]: [string, any]) => ({
    status,
    count: data.count,
    percentage: totalCount > 0 ? (data.count / totalCount) * 100 : 0,
    total_value: data.total_value,
  }));

  // Calculate monthly trends using UTC month buckets
  // Determine how many months the range spans and build buckets
  const startUtcMonth = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
  const endUtcMonth = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 1, 1));
  const monthSpan = (endUtcMonth.getUTCFullYear() - startUtcMonth.getUTCFullYear()) * 12
    + (endUtcMonth.getUTCMonth() - startUtcMonth.getUTCMonth());

  const trendBuckets: Array<{ start: Date; end: Date; label: string; count: number; total_value: number; processing_times: number[] }> = [];
  for (let i = 0; i < monthSpan; i++) {
    const bStart = new Date(Date.UTC(startUtcMonth.getUTCFullYear(), startUtcMonth.getUTCMonth() + i, 1));
    const bEnd = new Date(Date.UTC(bStart.getUTCFullYear(), bStart.getUTCMonth() + 1, 1));
    trendBuckets.push({ start: bStart, end: bEnd, label: format(bStart, 'MMM yyyy'), count: 0, total_value: 0, processing_times: [] });
  }

  procurements.forEach(p => {
    const d = new Date(p.created_at);
    for (const bucket of trendBuckets) {
      if (d >= bucket.start && d < bucket.end) {
        bucket.count += 1;
        bucket.total_value += p.total_amount || 0;
        if (p.status === 'completed' && p.delivery_date) {
          const created = new Date(p.created_at);
          const delivered = new Date(p.delivery_date);
          bucket.processing_times.push(Math.floor((delivered.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)));
        }
        break;
      }
    }
  });

  const monthlyTrends: ProcurementTrend[] = trendBuckets.map(b => ({
    month: b.label,
    count: b.count,
    total_value: b.total_value,
    avg_processing_time: b.processing_times.length > 0
      ? b.processing_times.reduce((sum, t) => sum + t, 0) / b.processing_times.length
      : 0,
  }));

  // Calculate top products
  const productGroups = procurements?.reduce((acc, p) => {
    if (!p.product_name) return acc;

    const productName = p.product_name;
    if (!acc[productName]) {
      acc[productName] = { count: 0, total_quantity: 0, total_value: 0 };
    }
    acc[productName].count += 1;
    acc[productName].total_quantity += p.total_quantity || 0;
    acc[productName].total_value += p.total_amount || 0;
    return acc;
  }, {} as Record<string, { count: number; total_quantity: number; total_value: number }>);

  const topProducts: TopProduct[] = Object.entries(productGroups || {})
    .map(([product_name, data]: [string, any]) => ({
      product_name,
      count: data.count,
      total_quantity: data.total_quantity,
      total_value: data.total_value,
    }))
    .sort((a, b) => b.total_value - a.total_value)
    .slice(0, 10);

  // Calculate rejection analysis
  const rejectedProcurements = procurements?.filter(p => p.status === 'rejected') || [];
  const rejectionGroups = rejectedProcurements.reduce((acc, p) => {
    const source = p.rejection_source || 'unknown';
    if (!acc[source]) {
      acc[source] = { count: 0, reasons: [] };
    }
    acc[source].count += 1;
    return acc;
  }, {} as Record<string, { count: number; reasons: string[] }>);

  const totalRejected = rejectedProcurements.length;
  const rejectionAnalysis: RejectionAnalysis[] = Object.entries(rejectionGroups).map(([source, data]: [string, any]) => ({
    rejection_source: source,
    count: data.count,
    percentage: totalRejected > 0 ? (data.count / totalRejected) * 100 : 0,
    common_reasons: data.reasons,
  }));

  // Calculate overall metrics
  const totalValue = procurements?.reduce((sum, p) => sum + (p.total_amount || 0), 0) || 0;
  const totalQuantity = procurements?.reduce((sum, p) => sum + (p.total_quantity || 0), 0) || 0;
  const completedProcurements = procurements?.filter(p => p.status === 'completed') || [];
  const processingTimes = completedProcurements
    .filter(p => p.delivery_date)
    .map(p => {
      const created = new Date(p.created_at);
      const delivered = new Date(p.delivery_date!);
      return Math.floor((delivered.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
    });

  const averageProcessingTime = processingTimes.length > 0
    ? processingTimes.reduce((sum, time) => sum + time, 0) / processingTimes.length
    : 0;

  return {
    statusStats,
    monthlyTrends,
    topProducts,
    rejectionAnalysis,
    averageProcessingTime,
    totalValue,
    totalCount,
    totalQuantity,
  };
};

// Supplier Performance Report Service
export const getSupplierPerformanceData = async (
  startDate?: Date,
  endDate?: Date
): Promise<SupplierPerformanceData> => {
  const start = startDate || startOfYear(new Date());
  const end = endDate || new Date();

  console.log('Fetching supplier performance data from', start, 'to', end);

  // Get all supplier procurements in date range
  const { data: procurements, error: procurementsError } = await supabase
    .from('procurements')
    .select(`
      *,
      procurement_costs(*),
      procurement_finance(*),
      procurement_warehouse(*)
    `)
    .eq('source_type', 'supplier')
    .not('source_id', 'is', null)
    .gte('created_at', start.toISOString())
    .lte('created_at', end.toISOString());

  if (procurementsError) {
    console.error('Error fetching supplier procurements:', procurementsError);
    throw procurementsError;
  }

  // Get supplier details
  const supplierIds = [...new Set(procurements?.map(p => p.source_id).filter(Boolean))];
  const { data: suppliers, error: suppliersError } = await supabase
    .from('suppliers')
    .select('id, name')
    .in('id', supplierIds);

  if (suppliersError) {
    console.error('Error fetching suppliers:', suppliersError);
    throw suppliersError;
  }

  // Create supplier name mapping
  const supplierMap = suppliers?.reduce((acc, s) => {
    acc[s.id] = s.name;
    return acc;
  }, {} as Record<string, string>) || {};

  // Calculate supplier rankings
  const supplierGroups = procurements?.reduce((acc, p) => {
    if (!p.source_id) return acc;

    const supplierId = p.source_id;
    if (!acc[supplierId]) {
      acc[supplierId] = {
        total_procurements: 0,
        completed_procurements: 0,
        rejected_procurements: 0,
        total_value: 0,
        delivery_times: [],
        rejections: 0,
      };
    }

    acc[supplierId].total_procurements += 1;
    acc[supplierId].total_value += p.total_amount || 0;

    if (p.status === 'completed') {
      acc[supplierId].completed_procurements += 1;

      if (p.expected_delivery_date && p.delivery_date) {
        const expected = new Date(p.expected_delivery_date);
        const actual = new Date(p.delivery_date);
        const deliveryTime = Math.floor((actual.getTime() - expected.getTime()) / (1000 * 60 * 60 * 24));
        acc[supplierId].delivery_times.push(deliveryTime);
      }
    }

    if (p.status === 'rejected') {
      acc[supplierId].rejected_procurements += 1;
      acc[supplierId].rejections += 1;
    }

    return acc;
  }, {} as Record<string, {
    total_procurements: number;
    completed_procurements: number;
    rejected_procurements: number;
    total_value: number;
    delivery_times: number[];
    rejections: number;
  }>);

  const supplierRankings: SupplierRanking[] = Object.entries(supplierGroups || {}).map(([supplierId, data]) => {
    const completionRate = data.total_procurements > 0
      ? (data.completed_procurements / data.total_procurements) * 100
      : 0;

    const rejectionRate = data.total_procurements > 0
      ? (data.rejected_procurements / data.total_procurements) * 100
      : 0;

    const avgDeliveryTime = data.delivery_times.length > 0
      ? data.delivery_times.reduce((sum, time) => sum + time, 0) / data.delivery_times.length
      : 0;

    // Quality score: combination of completion rate and low rejection rate
    const qualityScore = completionRate * (1 - rejectionRate / 100);

    return {
      supplier_id: supplierId,
      supplier_name: supplierMap[supplierId] || 'Unknown Supplier',
      completion_rate: completionRate,
      total_procurements: data.total_procurements,
      completed_procurements: data.completed_procurements,
      total_value: data.total_value,
      avg_delivery_time: avgDeliveryTime,
      rejection_rate: rejectionRate,
      quality_score: qualityScore,
    };
  }).sort((a, b) => b.quality_score - a.quality_score);

  // Calculate delivery performance
  const deliveryPerformance: DeliveryPerformance[] = supplierRankings.map(supplier => {
    const supplierData = supplierGroups[supplier.supplier_id];
    const onTimeDeliveries = supplierData?.delivery_times.filter(time => time <= 0).length || 0;
    const lateDeliveries = supplierData?.delivery_times.filter(time => time > 0).length || 0;
    const avgDelayDays = supplierData?.delivery_times.filter(time => time > 0)
      .reduce((sum, time) => sum + time, 0) / Math.max(lateDeliveries, 1) || 0;

    return {
      supplier_id: supplier.supplier_id,
      supplier_name: supplier.supplier_name,
      on_time_deliveries: onTimeDeliveries,
      late_deliveries: lateDeliveries,
      avg_delay_days: avgDelayDays,
      performance_trend: 'stable' as const, // This would require historical analysis
    };
  });

  const topPerformers = supplierRankings.slice(0, 5);
  const underPerformers = supplierRankings.slice(-5).reverse();

  return {
    supplierRankings,
    deliveryPerformance,
    topPerformers,
    underPerformers,
  };
};

// Get procurement data for time range
export const getProcurementData = async (timeRange: string) => {
  let startDate: Date;
  const endDate = new Date();

  switch (timeRange) {
    case '1m':
      startDate = subDays(endDate, 30);
      break;
    case '3m':
      startDate = subDays(endDate, 90);
      break;
    case '6m':
      startDate = subDays(endDate, 180);
      break;
    case '1y':
      startDate = subDays(endDate, 365);
      break;
    default:
      startDate = subDays(endDate, 90);
  }

  console.log('Fetching procurement data from', startDate, 'to', endDate);

  const { data: procurements, error } = await supabase
    .from('procurements')
    .select(`
      *,
      procurement_costs(*),
      procurement_finance(*),
      procurement_warehouse(*),
      procurement_items(*)
    `)
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString())
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching procurement data:', error);
    throw error;
  }

  console.log('Fetched', procurements?.length || 0, 'procurements');

  if (!procurements || procurements.length === 0) {
    console.log('No procurements found for the selected time range');
    return [];
  }

  // Get all unique source IDs for batch fetching
  const supplierIds = [...new Set(procurements.filter(p => p.source_type === 'supplier' && p.source_id).map(p => p.source_id))];
  const agentIds = [...new Set(procurements.filter(p => p.source_type === 'agent' && p.source_id).map(p => p.source_id))];
  const farmIds = [...new Set(procurements.filter(p => p.source_type === 'farm' && p.source_id).map(p => p.source_id))];

  console.log('Fetching source names:', { suppliers: supplierIds.length, agents: agentIds.length, farms: farmIds.length });

  // Batch fetch all source names
  const [suppliers, agents, farms] = await Promise.all([
    supplierIds.length > 0 ? supabase.from('suppliers').select('id, name').in('id', supplierIds) : { data: [] },
    agentIds.length > 0 ? supabase.from('agents').select('id, name').in('id', agentIds) : { data: [] },
    farmIds.length > 0 ? supabase.from('farms').select('id, farmer_name').in('id', farmIds) : { data: [] }
  ]);

  // Create lookup maps
  const supplierMap = (suppliers.data || []).reduce((acc, s) => ({ ...acc, [s.id]: s.name }), {} as Record<string, string>);
  const agentMap = (agents.data || []).reduce((acc, a) => ({ ...acc, [a.id]: a.name }), {} as Record<string, string>);
  const farmMap = (farms.data || []).reduce((acc, f) => ({ ...acc, [f.id]: f.farmer_name }), {} as Record<string, string>);

  // Enrich procurements with source names
  const enrichedProcurements = procurements.map(procurement => {
    let sourceName = '';

    if (procurement.source_type === 'supplier' && procurement.source_id) {
      sourceName = supplierMap[procurement.source_id] || 'Unknown Supplier';
    } else if (procurement.source_type === 'agent' && procurement.source_id) {
      sourceName = agentMap[procurement.source_id] || 'Unknown Agent';
    } else if (procurement.source_type === 'farm' && procurement.source_id) {
      sourceName = farmMap[procurement.source_id] || 'Unknown Farm';
    } else if (procurement.source_type === 'harvest') {
      sourceName = 'Harvest';
    } else {
      sourceName = procurement.source_type || 'Unknown';
    }

    return {
      ...procurement,
      source_name: sourceName
    };
  });

  console.log('Enriched procurements with source names');
  return enrichedProcurements;
};

// Supplier Credit Underwriting Service
export const getSupplierUnderwritingData = async (): Promise<SupplierUnderwritingData[]> => {
  const { data, error } = await (supabase as any)
    .from('v_supplier_credit_underwriting')
    .select('*')
    .order('total_value_birr', { ascending: false });

  if (error) {
    console.error('Error fetching underwriting data:', error);
    throw error;
  }

  return data || [];
};

// Export utility for downloading reports
export const exportToCSV = (data: any[], filename: string) => {
  if (!data || data.length === 0) return;

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row =>
      headers.map(header => {
        const value = row[header];
        if (typeof value === 'string' && value.includes(',')) {
          return `"${value}"`;
        }
        return value || '';
      }).join(',')
    )
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};